import { FileText, Users, Clock, TrendingUp, Check } from 'lucide-react';

/**
 * Juno Submission Status Definitions
 * 
 * This is the single source of truth for all submission statuses.
 * Organized chronologically by pipeline stage.
 */

// Active Pipeline Statuses (Happy Path)
export const ACTIVE_PIPELINE_STATUSES = [
  {
    key: 'submission',
    status: 'Submission',
    label: 'Submission',
    description: 'New submissions in pipeline',
    color: 'blue',
    icon: FileText,
    category: 'active'
  },
  {
    key: 'clientSubmission',
    status: 'Client Submission',
    label: 'Client Submission',
    description: 'Submitted to clients',
    color: 'indigo',
    icon: Users,
    category: 'active'
  },
  {
    key: 'interview',
    status: 'Interview',
    label: 'Interview',
    description: 'Candidates in interviews',
    color: 'orange',
    icon: Clock,
    category: 'active'
  },
  {
    key: 'offerExtended',
    status: 'Offer Extended',
    label: 'Offer Extended',
    description: 'Offers extended to candidates',
    color: 'purple',
    icon: TrendingUp,
    category: 'active'
  },
  {
    key: 'offerAccepted',
    status: 'Offer Accepted',
    label: 'Offer Accepted',
    description: 'Accepted offers',
    color: 'green',
    icon: Check,
    category: 'active'
  }
];

// Terminal Statuses
export const TERMINAL_STATUSES = [
  {
    key: 'onboardingInitiated',
    status: ['Onboarding Initiated', 'Onboarding'], // Support both variants
    label: 'Onboarding Initiated',
    description: 'Onboarding in progress',
    color: 'teal',
    icon: Check,
    category: 'terminal-success'
  },
  {
    key: 'onboarded',
    status: 'Onboarded',
    label: 'Onboarded',
    description: 'Successfully onboarded',
    color: 'green',
    icon: Check,
    category: 'terminal-success'
  },
  {
    key: 'rejected',
    status: 'Rejected',
    label: 'Rejected',
    description: 'Generic rejection',
    color: 'rose',
    icon: FileText,
    category: 'terminal-rejected'
  },
  {
    key: 'interviewRejected',
    status: 'Interview Rejected',
    label: 'Interview Rejected',
    description: 'Failed interview',
    color: 'pink',
    icon: Clock,
    category: 'terminal-rejected'
  },
  {
    key: 'offerRejected',
    status: 'Offer Rejected',
    label: 'Offer Rejected',
    description: 'Candidate declined offer',
    color: 'red',
    icon: TrendingUp,
    category: 'terminal-rejected'
  }
];

// All statuses combined
export const ALL_SUBMISSION_STATUSES = [
  ...ACTIVE_PIPELINE_STATUSES,
  ...TERMINAL_STATUSES
];

// Helper function to match submission status
export const matchesStatus = (submission, statusDef) => {
  if (Array.isArray(statusDef.status)) {
    return statusDef.status.includes(submission.status);
  }
  return submission.status === statusDef.status;
};

// Get status definition by key
export const getStatusDef = (key) => {
  return ALL_SUBMISSION_STATUSES.find(s => s.key === key);
};

// Get status color classes for detail pane
export const getStatusColorClass = (key) => {
  const statusDef = getStatusDef(key);
  if (!statusDef) return 'bg-gray-100 text-gray-700';
  
  const colorMap = {
    blue: 'bg-blue-100 text-blue-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    orange: 'bg-orange-100 text-orange-700',
    purple: 'bg-purple-100 text-purple-700',
    green: 'bg-green-100 text-green-700',
    teal: 'bg-teal-100 text-teal-700',
    rose: 'bg-rose-100 text-rose-700',
    pink: 'bg-pink-100 text-pink-700',
    red: 'bg-red-100 text-red-700'
  };
  
  return colorMap[statusDef.color] || 'bg-gray-100 text-gray-700';
};
